// Appels d'API des INVITATIONS À UNE SÉANCE et des comptes de visioconférence.
//
// Trois publics : l'équipe (invitations d'une étude), chaque membre (SON compte
// Google ou Microsoft), et le participant — sans compte, par jeton.
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export type VisioProvider = 'google_meet' | 'teams';

export const PROVIDER_LABELS: Record<VisioProvider, string> = {
  google_meet: 'Google Meet',
  teams: 'Microsoft Teams',
};

export type InvitationStatus = 'pending' | 'accepted' | 'declined';

/** Le statut de RÉSERVATION (055), calculé par le serveur. */
export type BookingStatus = 'invite' | 'reserve' | 'modifie' | 'decline' | 'realise' | 'absent' | 'annule';
export type Outcome = 'done' | 'no_show' | 'cancelled';
export type FormatKind = 'google_meet' | 'teams' | 'onsite';

export const BOOKING_LABELS: Record<BookingStatus, { label: string; tone: string }> = {
  invite: { label: 'Invité', tone: 'tone-warn' },
  reserve: { label: 'Réservé', tone: 'tone-good' },
  modifie: { label: 'Modifié', tone: 'tone-good' },
  decline: { label: 'Décliné', tone: 'tone-bad' },
  realise: { label: 'Réalisé', tone: 'tone-good' },
  absent: { label: 'Absent', tone: 'tone-bad' },
  annule: { label: 'Annulé', tone: 'tone-empty' },
};

/** À distance (visioconférence) ou sur place (un lieu de rendez-vous). */
export type InvitationMode = 'remote' | 'onsite';

export interface SessionInvitation {
  id: string;
  project_id: string;
  study_id: string;
  invitee_name: string;
  invitee_email: string | null;
  subject: string;
  message: string | null;
  /** Vide tant que la personne n'a pas choisi parmi `slot_options`. */
  starts_at: string | null;
  /** Créneaux proposés au choix (instants ISO), ou `null` pour un créneau fixé. */
  slot_options: string[] | null;
  /** La personne de l'annuaire invitée, s'il y en a une. */
  person_id: string | null;
  duration_min: number;
  time_zone: string;
  mode: InvitationMode;
  /** Le lieu, pour un rendez-vous sur place. */
  location: string | null;
  /** L'outil de visioconférence : absent pour un rendez-vous sur place. */
  provider: VisioProvider | null;
  status: InvitationStatus;
  responded_at: string | null;
  decline_reason: string | null;
  meeting_url: string | null;
  /** Pourquoi le lien n'a pas pu être créé à l'acceptation. */
  meeting_error: string | null;
  created_at: string;
  /** Formats proposés au choix (055), et ce qui s'est passé après la séance. */
  format_options?: FormatKind[] | null;
  outcome?: Outcome | null;
  /** Dernier changement de créneau ou de format par la personne (057). */
  rescheduled_at?: string | null;
  booking: BookingStatus;
}

export interface InvitationPatch {
  inviteeName: string;
  inviteeEmail: string | null;
  subject: string;
  message: string | null;
  /** Un créneau fixé… */
  startsAt?: string;
  /** Les formats proposés (055) : plusieurs = la personne choisit. */
  formats?: FormatKind[];
  /** …ou plusieurs au choix : la personne choisira. */
  slots?: string[];
  /** Une personne de l'annuaire (son nom et son adresse viennent de là). */
  personId?: string;
  /** Programmé depuis la fiche d'un entretien : il portera ce rendez-vous. */
  interviewId?: string;
  durationMin: number;
  timeZone: string;
  mode?: InvitationMode;
  location: string | null;
  provider?: VisioProvider | null;
}

export interface VisioAccount {
  provider: VisioProvider;
  label: string;
  /** L'exploitant a déclaré l'application chez l'outil. */
  configured: boolean;
  connected: boolean;
  accountEmail: string | null;
  connectedAt: string | null;
}

/** Ce que lit le participant : rien du projet, rien de l'étude. */
export interface PublicSessionInvitation {
  organizationName: string;
  organizerName: string;
  inviteeName: string;
  subject: string;
  message: string | null;
  startsAt: string | null;
  /** La personne choisit son créneau parmi `slots` (ceux encore libres). */
  choosing: boolean;
  slots: string[];
  durationMin: number;
  mode: InvitationMode;
  location: string | null;
  provider: VisioProvider | null;
  providerLabel: string;
  status: InvitationStatus;
  ended: boolean;
  meetingUrl: string | null;
  /** Formats au choix (055), tant qu'elle n'a pas répondu ; et l'annulation par l'équipe. */
  formatOptions: { value: FormatKind; label: string }[];
  cancelled: boolean;
  /** Après réservation (057) : modifier (autre créneau, autre format) ou annuler. */
  canCancel: boolean;
  canReschedule: boolean;
  changeSlots: string[];
  changeFormats: { value: FormatKind; label: string }[];
  rescheduledAt: string | null;
}

// --- Équipe --------------------------------------------------------------------

const studyKey = (studyId: string) => ['session-invitations', studyId] as const;
const invalidate = (qc: QueryClient, studyId: string) => void qc.invalidateQueries({ queryKey: studyKey(studyId) });
const path = (id: string) => `/api/session-invitations/${encodeURIComponent(id)}`;

export function useStudyInvitations(studyId: string) {
  return useQuery({
    queryKey: studyKey(studyId),
    queryFn: () =>
      api.get<{ invitations: SessionInvitation[]; mail: boolean; demo: boolean }>(
        `/api/studies/${encodeURIComponent(studyId)}/invitations`,
      ),
    staleTime: 15 * 1000,
    // Une réponse peut arriver pendant qu'on regarde la fiche.
    refetchOnWindowFocus: true,
  });
}

export function useCreateInvitation(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: InvitationPatch) =>
      api.post<{ invitation: SessionInvitation; token: string; mailSent: boolean }>(
        `/api/studies/${encodeURIComponent(studyId)}/invitations`,
        patch,
      ),
    onSuccess: () => {
      invalidate(qc, studyId);
      void qc.invalidateQueries({ queryKey: ['interview'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/** Les champs communs d'un envoi à plusieurs personnes de l'annuaire. */
export type BatchPatch = Omit<InvitationPatch, 'inviteeName' | 'inviteeEmail'> & { personIds: string[] };

export interface BatchResult {
  sent: { personId: string; name: string; email: string | null; invitation: SessionInvitation; token: string; mailSent: boolean }[];
  skipped: { personId: string; reason: string }[];
}

/** Inviter plusieurs personnes de l'annuaire d'un coup : un lien chacune. */
export function useCreateInvitationBatch(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: BatchPatch) =>
      api.post<BatchResult>(`/api/studies/${encodeURIComponent(studyId)}/invitations/batch`, patch),
    onSuccess: () => {
      invalidate(qc, studyId);
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

/** Après la séance (055) : réalisé, absent, annulé — ou `null` pour revenir à la réponse. */
export function useSetOutcome(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome: Outcome | null }) =>
      api.put<{ invitation: SessionInvitation }>(`${path(id)}/outcome`, { outcome }),
    onSuccess: () => invalidate(qc, studyId),
  });
}

/** Les invitations partiront-elles toutes seules par e-mail ? */
export function useMailStatus() {
  return useQuery({
    queryKey: ['invitations', 'mail'] as const,
    queryFn: () => api.get<{ configured: boolean; from: string | null }>('/api/invitations/mail'),
    staleTime: 60 * 1000,
  });
}

export function useRotateInvitation() {
  return useMutation({
    mutationFn: (id: string) => api.post<{ id: string; token: string }>(`${path(id)}/rotate`),
  });
}

export function useDeleteInvitation(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    // `notified` : un rendez-vous réservé a d'abord été annulé auprès de la personne.
    mutationFn: (id: string) => api.del<{ deleted: boolean; notified: boolean }>(path(id)),
    onSuccess: () => invalidate(qc, studyId),
  });
}

/** Proposer une autre date : nouveaux créneaux, nouveau lien, e-mail à la personne. */
export function useReproposeInvitation(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, slots, message }: { id: string; slots: string[]; message?: string }) =>
      api.post<{ invitation: SessionInvitation; token: string; mailSent: boolean }>(`${path(id)}/repropose`, { slots, message }),
    onSuccess: () => invalidate(qc, studyId),
  });
}

/** Coller un lien créé soi-même dans Meet ou Teams. */
export function useSetMeetingUrl(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, url }: { id: string; url: string }) =>
      api.put<{ invitation: SessionInvitation }>(`${path(id)}/meeting`, { url }),
    onSuccess: () => invalidate(qc, studyId),
  });
}

/** Créer le lien avec MON compte connecté. */
export function useGenerateMeeting(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ invitation: SessionInvitation }>(`${path(id)}/meeting`),
    onSuccess: () => invalidate(qc, studyId),
  });
}

// --- Mon compte de visioconférence ----------------------------------------------

const ACCOUNTS = ['visio', 'accounts'] as const;

export function useVisioAccounts() {
  return useQuery({
    queryKey: ACCOUNTS,
    queryFn: () => api.get<{ demo: boolean; providers: VisioAccount[] }>('/api/visio/accounts'),
    staleTime: 30 * 1000,
  });
}

/** Rend l'adresse d'autorisation : c'est le navigateur entier qui y part. */
export function useConnectVisio() {
  return useMutation({
    mutationFn: (provider: VisioProvider) => api.post<{ url: string }>(`/api/visio/${provider}/connect`),
  });
}

export function useDisconnectVisio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: VisioProvider) => api.del<{ disconnected: boolean }>(`/api/visio/${provider}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ACCOUNTS }),
  });
}

// --- Participant (public, par jeton) ----------------------------------------------

const publicKey = (token: string) => ['public-session-invitation', token] as const;
const publicPath = (token: string) => `/api/public/session-invitations/${encodeURIComponent(token)}`;

export function usePublicSessionInvitation(token: string) {
  return useQuery({
    queryKey: publicKey(token),
    queryFn: () => api.get<{ invitation: PublicSessionInvitation }>(publicPath(token)).then((b) => b.invitation),
    retry: false,
    // Accepté sans lien encore : l'équipe va l'ajouter, on le guette.
    refetchInterval: (q) => (q.state.data?.status === 'accepted' && !q.state.data.meetingUrl ? 30_000 : false),
  });
}

export function useRespondInvitation(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (answer: { accept?: boolean; action?: 'reschedule' | 'cancel'; reason?: string; startsAt?: string; format?: FormatKind }) =>
      api.post<{ invitation: PublicSessionInvitation }>(`${publicPath(token)}/respond`, answer).then((b) => b.invitation),
    onSuccess: (invitation) => qc.setQueryData(publicKey(token), invitation),
    // Créneau pris entre-temps : on relit les créneaux encore libres.
    onError: () => void qc.invalidateQueries({ queryKey: publicKey(token) }),
  });
}
